import { AspectRatio, Button, Image } from '@amzn/ring-ui-react-components';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  useNeighborsWebClickEventStreamDispatcher,
  useNeighborsWebViewEventStreamDispatcher,
} from 'src/components/App/hooks';
import { useAuth } from 'src/context';
import { VideoGrid } from 'src/features/BestOfRing/components/VideoGrid';
import { SubmitVideoModal } from 'src/features/BestOfRing/modals/SubmitVideoModal';
import { Container } from 'src/features/Deals/components/Container';
import { useHasWebViewUserAgent } from 'src/hooks';
import { Box } from 'src/shared/components/Box';
import { Icon } from 'src/shared/components/Icon';
import { CenteredLoading } from 'src/shared/components/Loading';
import { useOnScreen, useShareButtonAction } from 'src/shared/hooks';
import { reLoginWebview } from 'src/utils';
import { UploadYourVideoButton } from '../components/UploadYourVideoButton';
import { UploadYourVideoFAB } from '../components/UploadYourVideoFAB';
import { useBestOfRingContext } from '../context';

const maxWidth = '500px';

const Avatar = () => (
  <Image
    data-testid="best-of-ring-avatar"
    position="absolute"
    left={6}
    top="-60px"
    src="/assets/images/top-ring-videos.svg"
    width="80px"
    height="80px"
    zIndex={1}
  />
);

export const VideoFeedPage = () => {
  const { gallery, loading, videoList } = useBestOfRingContext();

  const share = useShareButtonAction({
    shareUrl: window.location.href.split('?')[0],
    title: document.title,
    referringItem: 'nhWeb_bestOfRingGallery',
  });
  const isWebview = useHasWebViewUserAgent();
  const { isAuthenticated, isPending, isUnauthenticated } = useAuth();
  const [showFAB, setShowFAB] = useState<boolean>(false);
  const [isSubmitVideoModalOpen, setIsSubmitVideoModalOpen] =
    useState<boolean>(false);

  const uploadButtonRef = useOnScreen(([entry]) => {
    setShowFAB(!entry.isIntersecting);
  }, 1);

  const dispatchView =
    useNeighborsWebViewEventStreamDispatcher('nhWeb_bestOfRing');
  const pageViewRef = useRef(false);

  const dispatchClick =
    useNeighborsWebClickEventStreamDispatcher('nhWeb_bestOfRing');

  useEffect(() => {
    if (pageViewRef.current === true) return;
    const searchParams = new URLSearchParams(window.location.search);
    const source = searchParams.get('source');
    if (source === 'share') {
      dispatchView([''], { referringItem: 'nhWeb_bestOfRingShare' });
    } else if (source === 'tile') {
      dispatchView([''], { referringItem: 'nhWeb_bestOfRingTile' });
    } else {
      dispatchView(['']);
    }
    pageViewRef.current = true;
  }, [dispatchView]);

  const onShare = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_shareBestOfRing', [''], {
        itemType: 'button',
      });
    }
    share();
  };

  const onSubmitVideo = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_submitYourVideo', [], { itemType: 'button' });
    }
    setIsSubmitVideoModalOpen(true);
  };

  const onSubmitVideoClose = () => {
    setIsSubmitVideoModalOpen(false);
  };

  useEffect(() => {
    if (isWebview && isUnauthenticated && !isPending) {
      reLoginWebview();
    }
  }, [isUnauthenticated, isWebview, isPending]);

  if (loading || isPending || (isWebview && isUnauthenticated)) {
    return <CenteredLoading data-testid="loading-spinner" />;
  }

  return (
    <>
      <SubmitVideoModal
        isOpen={isSubmitVideoModalOpen}
        onClose={onSubmitVideoClose}
      />
      <Box display="flex" justifyContent="center">
        <Helmet>
          <title>Best of Ring</title>
        </Helmet>
        <Box maxWidth={maxWidth} display="flex" flexDirection="column">
          <AspectRatio ratio={16 / 9}>
            <Image
              height="100%"
              src={gallery?.backgroundImage?.url!}
              data-testid="header-image"
              objectFit="contain"
            />
          </AspectRatio>
          <Container
            px={6}
            position="relative"
            maxWidth={maxWidth}
            borderBottom="1px solid"
            borderBottomColor="tertiary.500"
          >
            <Avatar />
            <Box display="flex" flexDirection="column" mb={24} mt={36}>
              <Box
                mb="8px"
                textStyle="heading-small-medium"
                fontSize="24px"
                lineHeight="36px"
                data-testid="header-title"
              >
                {gallery?.title}
              </Box>
              <Box
                textStyle="body-large-light"
                data-testid="header-description"
              >
                {gallery?.description}
              </Box>
            </Box>
            <Box display="flex" gridGap={6} pb={6}>
              {isAuthenticated && (
                <div ref={uploadButtonRef}>
                  <UploadYourVideoButton onClick={onSubmitVideo} />
                </div>
              )}
              <Button
                pr={13}
                borderRadius="full"
                size="xs"
                height="36px"
                textStyle="body-large-bold"
                onClick={onShare}
                data-testid="header-share-button"
              >
                <Icon name="share" color="white" size={24} mr={1} />
                Share
              </Button>
            </Box>
          </Container>
          {videoList && (
            <Box m={2}>
              <VideoGrid videos={videoList} />
            </Box>
          )}
          {isAuthenticated && showFAB && (
            <UploadYourVideoFAB
              aria-label="Submit your video"
              onClick={onSubmitVideo}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
